import { IconButton, Tooltip } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useOnViewportChange, useReactFlow } from 'reactflow';
import { useAuthStore } from '../../store/storeAuth';
import QuickChatIcon from '../Common/QuickChatIcon';
import NodeAssistantChat from './NodeAssistantChat';
import { NodeAssistantProps } from './types';

const NodeAssistant = ({
  nodeId,
  nodeType,
  nodeData,
  edges,
  flowSummary,
}: NodeAssistantProps) => {
  const { user } = useAuthStore(state => state);
  const isDark = user?.theme === 'dark';
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const initialPosRef = useRef<{ left: number; top: number } | null>(null);
  const canvasPosRef = useRef<{ x: number; y: number } | null>(null);
  const containerElRef = useRef<Element | null>(null);
  const { getViewport } = useReactFlow();

  useOnViewportChange({
    onChange: ({ x: vpX, y: vpY, zoom }) => {
      if (!open || !popupRef.current || !canvasPosRef.current || !containerElRef.current) return;
      const cr = containerElRef.current.getBoundingClientRect();
      popupRef.current.style.left = `${canvasPosRef.current.x * zoom + vpX + cr.left}px`;
      popupRef.current.style.top = `${canvasPosRef.current.y * zoom + vpY + cr.top}px`;
    },
  });

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  const handleOpen = () => {
    if (anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      const container = anchorRef.current.closest('.react-flow');
      const containerRect = container?.getBoundingClientRect() ?? { left: 0, top: 0 };
      const { x: vpX, y: vpY, zoom } = getViewport();
      canvasPosRef.current = {
        x: (rect.right - containerRect.left - vpX) / zoom,
        y: (rect.top - containerRect.top - vpY) / zoom,
      };
      containerElRef.current = container;
      initialPosRef.current = { left: rect.right, top: rect.top };
    }
    setOpen(true);
  };

  return (
    <>
      <Tooltip
        title="Quick Chat"
        arrow
        placement="top"
        componentsProps={{
          tooltip: { sx: { bgcolor: isDark ? '#101828' : '#475467' } },
          arrow: { sx: { color: isDark ? '#101828' : '#475467' } },
        }}
      >
        <IconButton
          ref={anchorRef}
          onClick={handleOpen}
          className="nodrag"
          sx={{
            p: '2px',
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
            },
          }}
        >
          <QuickChatIcon size="sm"/>
        </IconButton>
      </Tooltip>

      {open && initialPosRef.current && createPortal(
        <div
          ref={popupRef}
          style={{
            position: 'fixed',
            left: initialPosRef.current.left,
            top: initialPosRef.current.top,
            translate: '-100% 0',
            borderRadius: '12px',
            boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.15)',
            overflow: 'hidden',
            zIndex: 9999,
          }}
        >
          <NodeAssistantChat
            nodeId={nodeId}
            nodeType={nodeType}
            nodeData={nodeData}
            edges={edges}
            flowSummary={flowSummary}
            onClose={() => setOpen(false)}
          />
        </div>,
        document.body
      )}
    </>
  );
};

export default NodeAssistant;
